import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  DatePicker,
  DatePickerProps,
  Form,
  Input,
  Progress,
  Select,
  Spin,
  Tooltip,
  message,
} from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import { useEffect, useRef, useState } from 'react';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { socketHelper } from 'src/app/helpers';
import { formatDate } from 'src/app/helpers/time.helper';
import { useNumberMutation, useStartMutation } from 'src/app/redux/blackSystem/blackSystem.slice';
import { useAppSelector } from 'src/app/redux/hook';
import { axios } from 'src/app/utils';
import { systemConfig } from 'src/configs';
import { finalUrlsConfig } from 'src/configs/url.config';
import { XEnum } from 'src/enums/twitter.enum';
import { UploadIcon } from 'src/icons';

const keysForQuery = [XEnum.tweets_of_last_seven_days, XEnum.tweets_popular_count];
const keysForUserId = [XEnum.tweets_of_user];
const keysForTweetId = [XEnum.tweet_informations, XEnum.retweet_informations];
const keysForTweetIdAndUserId = [XEnum.tweet_like_count];
// const keysForUserName = [XEnum.user_informations];

const mediaTypes = ['top_media', 'recent_media'];

const numberValidator = (_: any, value: any, key: string) => {
  if (!value) return Promise.resolve();

  const parsedValue = Number(value);
  if (Number.isNaN(parsedValue)) {
    return Promise.reject(new Error(`Please enter a valid ${key}`));
  }

  return Promise.resolve();
};
const now = dayjs();
const min = now.subtract(10, 'year');
const max = now.add(10, 'year');
function SocialMediaComponent() {
  const [submitType, setSubmitType] = useState<string>('');

  const [socialMediaForm] = useForm();
  const [zipCount, setZipCount] = useState<number>(0);

  const [start] = useStartMutation();
  const [number, { isLoading: isNumberLoading }] = useNumberMutation();
  const [numberMessage, setNumberMessage] = useState<string | number>('');
  const { theme } = useAppSelector((state) => state.configContent);
  const [allProgress, setAllProgress] = useState<any[]>([]);
  const [eventProgress, setEventProgress] = useState<any>({});
  const [startProcess, setStartProcess] = useState<string>('');

  const [downloadState, setDownloadState] = useState<{
    state: boolean;
    done: boolean;
    size: string;
  }>({
    state: false,
    done: false,
    size: '0.00',
  });
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const socialMedias = systemConfig.sensorUrls.socialMediaUrls;
  const selectedSocialMedia = useWatch('socialMedia', socialMediaForm);
  const selectedTweetType = useWatch('tweetType', socialMediaForm);
  const enteredTag = useWatch('tag', socialMediaForm);
  const query = useWatch('query', socialMediaForm);
  const userId = useWatch('user_id', socialMediaForm);
  const tweedId = useWatch('tweet_id', socialMediaForm);
  // const tag = useWatch('tag', socialMediaForm);
  const [toDate, setToDate] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const fromDateValue = Form.useWatch('fromDate', socialMediaForm);
  const toDateValue = Form.useWatch('toDate', socialMediaForm);

  const numberTimeoutRef: any = useRef(null);
  const exportTimeoutRef: any = useRef(null);
  const startProcessRef = useRef(startProcess);

  useEffect(() => {
    socketHelper.createConnection({ path: '/oss-progress' });
  }, []);

  useEffect(() => {
    startProcessRef.current = startProcess;
  }, [startProcess]);

  useEffect(() => {
    const socketData = socketHelper.listenSocket('event_progress', (data) => {
      if (startProcessRef.current === data.job.id) {
        setEventProgress(data.job);
      }
    });
  }, [socketHelper]);

  useEffect(() => {
    if (isNumberLoading) {
      numberTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForNumberResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(numberTimeoutRef.current);
    }

    return () => {
      clearTimeout(numberTimeoutRef.current);
    };
  }, [isNumberLoading]);

  useEffect(() => {
    if (isExportLoading) {
      exportTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForExportResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(exportTimeoutRef.current);
    }

    return () => {
      clearTimeout(exportTimeoutRef.current);
    };
  }, [isExportLoading]);

  const xOptions = [
    { label: t('blackSystemPage.OSS.tweetsOfLastSevenDays'), key: XEnum.tweets_of_last_seven_days },
    { label: t('blackSystemPage.OSS.userTweets'), key: XEnum.tweets_of_user },
    { label: t('blackSystemPage.OSS.tweetInformation'), key: XEnum.tweet_informations },
    { label: t('blackSystemPage.OSS.popularTweetCount'), key: XEnum.tweets_popular_count },
    { label: t('blackSystemPage.OSS.retweetInformation'), key: XEnum.retweet_informations },
    { label: t('blackSystemPage.OSS.tweetLikeCount'), key: XEnum.tweet_like_count },
  ];

  const debounceTimeoutRef: any = useRef(null);

  const handleNumberReq = () => {
    if (isNumberLoading) return;
    setNumberMessage('');
    setDownloadState({
      state: false,
      done: false,
      size: '0.00',
    });
    setZipCount(0);

    const formValuesAndURL = getFormValuesAndURL();

    let { formValues } = formValuesAndURL;
    const { reqUrl } = formValuesAndURL;

    if (toDateValue && fromDateValue) {
      formValues = { ...formValues, ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) };
    }

    const isInvalid = Object.keys(formValues).some((key) => !formValues[key]);
    if (isInvalid) return;

    number({ data: { formValues }, url: reqUrl })
      .unwrap()
      .then((res) => {
        if (res.oldest_folder_date) {
          setNumberMessage(
            `${res.number} ${t('blackSystemPage.newsApi.numberMessageCount')} ${res.oldest_folder_date}`,
          );
          setZipCount(res.number);
        } else {
          setNumberMessage(() => {
            if (toDateValue && fromDateValue) {
              const formattedToDate = formatDate(toDateValue);
              const formattedFromDate = formatDate(fromDateValue);
              return `${res.number}, between ${formattedToDate} & ${formattedFromDate}`;
            }
            return `${res.number}`;
          });
          setZipCount(res.number);
        }
      })
      .catch(() => {
        setNumberMessage('');
        setZipCount(0);
        message.error(t('blackSystemPage.numberOperationFailed'), 5);
      });
  };

  useEffect(() => {
    const handleAPIRequest = () => {
      clearTimeout(debounceTimeoutRef.current);

      debounceTimeoutRef.current = setTimeout(() => {
        handleNumberReq();
      }, 500);
    };

    handleAPIRequest();

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [enteredTag, selectedSocialMedia, selectedTweetType, userId, tweedId, query, toDateValue, fromDateValue]);

  function getFormValuesAndURL() {
    let requestJSONKeys: any;

    if (selectedSocialMedia === 'X') {
      requestJSONKeys = keysForQuery.includes(selectedTweetType)
        ? ['query', 'tweetType']
        : keysForUserId.includes(selectedTweetType)
          ? ['user_id', 'tweetType']
          : keysForTweetId.includes(selectedTweetType)
            ? ['tweet_id', 'tweetType']
            : keysForTweetIdAndUserId.includes(selectedTweetType)
              ? ['tweet_id', 'user_id', 'tweetType']
              : null;
      // : keysForUserName.includes(selectedTweetType)
      //   ? ['user_name', 'tweetType']
      //   : null;
    }

    const formValues =
      selectedSocialMedia === 'Instagram'
        ? socialMediaForm.getFieldsValue(['tag', 'mediaType'])
        : socialMediaForm.getFieldsValue(requestJSONKeys);

    const reqUrl = selectedSocialMedia === 'X' ? 'Twitter' : selectedSocialMedia;

    return { formValues, reqUrl };
  }

  const onFinishSocialMedia = async () => {
    /* eslint-disable prefer-const */
    let { formValues, reqUrl } = getFormValuesAndURL();
    const submitObj = { ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) };
    formValues = { ...formValues, ...submitObj };
    if (submitType === 'Start') {
      if (zipCount > 0) {
        showModal({
          title: t('blackSystemPage.startStartOperation'),
          content: <p>{t('blackSystemPage.startOperationWarnText')}</p>,
          onSave: (modal) => {
            start({ data: { formValues }, url: reqUrl })
              .unwrap()
              .then((payload) => {
                // console.log('payload.job_id zipcount>0', payload.job_id);
                setStartProcess(payload.job_id);
              })
              .catch(() => message.error('Search Operation have been failed...'));
            modal.destroy();
            message.success(t('blackSystemPage.startOperationSuccess'), 5);
          },
          okText: t('blackSystemPage.startOkText'),
        });
      } else {
        message.success(t('blackSystemPage.startOperationSuccess'), 5);
        start({ data: { formValues }, url: reqUrl })
          .unwrap()
          .then((payload) => {
            // console.log('payload.job_id', payload.job_id);
            setStartProcess(payload.job_id);
          })
          .catch(() => message.error('Search Operation have been failed...'));
      }
    } else if (submitType === 'Export') {
      try {
        setIsExportLoading(true);
        message.success(t('blackSystemPage.exportOperationSuccess'), 5);
        await axios({
          url: finalUrlsConfig().blackSystem(reqUrl).export,
          method: 'POST',
          data: { formValues },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const bytesDownloaded = progressEvent.loaded;
            const megabytesDownloaded = bytesDownloaded / (1024 * 1024);
            if (megabytesDownloaded) {
              if (megabytesDownloaded > 1) {
                setIsExportLoading(false);
              }
              setDownloadState((prevState) => ({
                ...prevState,
                state: true,
                size: `${megabytesDownloaded.toFixed(2)}`,
              }));
            }
          },
          blockSpinner: true,
        })
          .then((res: any) => {
            const today = new Date();
            const formattedDate = formatDate(today);
            setDownloadState({
              size: '0.00',
              state: false,
              done: true,
            });
            fileDownload(res.data, `${selectedSocialMedia}-${formattedDate}.zip`);
            setIsExportLoading(false);
          })
          .catch(() => {
            message.error(t('blackSystemPage.exportOperationFailed'), 5);
            setDownloadState({
              done: false,
              size: '0.00',
              state: false,
            });
            setIsExportLoading(false);
          });
      } catch (error) {
        setIsExportLoading(false);
        console.log('err', error);
      }
    }
  };

  const disableConditions = ({ isExport, isStart }: { isExport?: boolean; isStart?: boolean }) => {
    if (isStart) {
      if (selectedSocialMedia === 'X') {
        if (keysForQuery.includes(selectedTweetType)) {
          if (!query) return true;
        } else if (keysForUserId.includes(selectedTweetType)) {
          if (!userId) return true;
        } else if (keysForTweetId.includes(selectedTweetType)) {
          if (!tweedId) return true;
        } else if (keysForTweetIdAndUserId.includes(selectedTweetType)) {
          if (!tweedId || !userId) return true;
        }
      } else if (selectedSocialMedia === 'Instagram') {
        if (!enteredTag) return true;
      }
    }

    if (isExport) {
      if (isExportLoading) return true;
      if (zipCount === 0 || !zipCount) {
        return true;
      }
    }
    return false;
  };

  const disabledDate = (current: dayjs.Dayjs, compareDate?: dayjs.Dayjs, isToDate?: boolean) => {
    const sevenYearsAgo = dayjs().subtract(7, 'year');

    if (isToDate && compareDate) {
      return current && (current.isAfter(dayjs().endOf('day')) || current.isBefore(compareDate));
    }

    if (current && current.isBefore(sevenYearsAgo, 'day')) {
      return true;
    }

    return current && current.isAfter(dayjs().endOf('day'));
  };
  const onFromDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setFromDate(formattedDate);
  };

  const onToDateChange: DatePickerProps['onChange'] = (_date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setToDate(formattedDate);
  };

  return (
    <div className="social-media">
      <div className="first-form">
        <div className="news-api-form">
          <FormComponent
            extraButton={
              <div className="extra-btns">
                <ExtraTab
                  onClick={() => {
                    setSubmitType('Start');
                    socialMediaForm.submit();
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.start')}
                  bgTheme
                  disabled={disableConditions({ isStart: true })}
                />

                <ExtraTab
                  onClick={() => {
                    setSubmitType('Export');
                    socialMediaForm.submit();
                  }}
                  icon={<UploadIcon />}
                  text={t('blackSystemPage.commonBtns.exportData')}
                  bgTheme
                  disabled={disableConditions({ isExport: true })}
                />
              </div>
            }
            options={{ labelCol: { span: 9 }, form: socialMediaForm, size: 'middle', onFinish: onFinishSocialMedia }}
          >
            <Form.Item
              name="socialMedia"
              label={
                <div style={{ display: 'flex' }}>
                  {t('blackSystemPage.OSS.selectSocialMedia')}
                  <Tooltip placement="topRight" title={t('blackSystemPage.OSS.selectSocialMedia')}>
                    <Badge offset={[10, 0]} />
                  </Tooltip>
                </div>
              }
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={socialMedias[0]}
            >
              <SearchableSelect bordered={false}>
                {socialMedias.map((socialMedia) => (
                  <Select.Option value={socialMedia} key={socialMedia}>
                    {socialMedia}
                  </Select.Option>
                ))}
              </SearchableSelect>
            </Form.Item>
            {selectedSocialMedia === 'Instagram' ? (
              <>
                <Form.Item name="tag" label={t('blackSystemPage.OSS.selectTag')} rules={[{ required: true }]}>
                  <Input variant="borderless" placeholder={t('blackSystemPage.OSS.enterATag')} />
                </Form.Item>
                <Form.Item
                  name="mediaType"
                  label={
                    <div style={{ display: 'flex' }}>
                      {t('blackSystemPage.OSS.selectMediaType')}
                      <Tooltip placement="topRight" title={t('blackSystemPage.OSS.selectMediaType')}>
                        <Badge offset={[10, 0]} />
                      </Tooltip>
                    </div>
                  }
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  initialValue={mediaTypes[0]}
                >
                  <SearchableSelect bordered={false}>
                    {mediaTypes.map((mediaType) => (
                      <Select.Option value={mediaType} key={mediaType}>
                        {mediaType}
                      </Select.Option>
                    ))}
                  </SearchableSelect>
                </Form.Item>
              </>
            ) : selectedSocialMedia === 'X' ? (
              <>
                <Form.Item
                  name="tweetType"
                  label={
                    <div style={{ display: 'flex' }}>
                      {t('blackSystemPage.OSS.tweetType')}
                      <Tooltip placement="topRight" title={t('blackSystemPage.OSS.tweetType')}>
                        <Badge offset={[10, 0]} />
                      </Tooltip>
                    </div>
                  }
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  initialValue={xOptions[0].key}
                >
                  <SearchableSelect bordered={false}>
                    {xOptions.map((tweetType) => (
                      <Select.Option value={tweetType.key} key={tweetType.key}>
                        {tweetType.label}
                      </Select.Option>
                    ))}
                  </SearchableSelect>
                </Form.Item>
                {keysForQuery.includes(selectedTweetType) ? (
                  <Form.Item name="query" label={t('blackSystemPage.OSS.enterAKey')} rules={[{ required: true }]}>
                    <Input variant="borderless" placeholder={t('blackSystemPage.OSS.enterAKey')} />
                  </Form.Item>
                ) : keysForUserId.includes(selectedTweetType) ? (
                  <Form.Item
                    name="user_id"
                    label={t('blackSystemPage.OSS.enterUserId')}
                    rules={[
                      {
                        required: true,
                        message: t('blackSystemPage.OSS.userIdRequired'),
                      },
                      {
                        validator: (_, value) => {
                          return numberValidator(_, value, 'User ID');
                        },
                      },
                    ]}
                  >
                    <Input variant="borderless" />
                  </Form.Item>
                ) : keysForTweetId.includes(selectedTweetType) ? (
                  <Form.Item
                    name="tweet_id"
                    label={t('blackSystemPage.OSS.enterTweetId')}
                    rules={[
                      {
                        required: true,
                        message: t('blackSystemPage.OSS.tweetIdRequired'),
                      },
                      {
                        validator: (_, value) => {
                          return numberValidator(_, value, 'Tweet ID');
                        },
                      },
                    ]}
                  >
                    <Input variant="borderless" />
                  </Form.Item>
                ) : keysForTweetIdAndUserId.includes(selectedTweetType) ? (
                  <>
                    <Form.Item
                      name="tweet_id"
                      label={t('blackSystemPage.OSS.enterTweetId')}
                      rules={[
                        {
                          required: true,
                          validator(_, value, callback) {
                            if (value || socialMediaForm.getFieldValue('user_id')) {
                              callback();
                            } else {
                              callback(t('blackSystemPage.OSS.provideTweetOrUserId'));
                            }
                          },
                        },
                        {
                          required: true,
                          validator: (_, value) => {
                            return numberValidator(_, value, 'Tweet ID');
                          },
                        },
                      ]}
                    >
                      <Input variant="borderless" />
                    </Form.Item>
                    <Form.Item
                      name="user_id"
                      label={t('blackSystemPage.OSS.enterUserId')}
                      rules={[
                        {
                          required: true,
                          validator(_, value, callback) {
                            if (value || socialMediaForm.getFieldValue('tweet_id')) {
                              callback();
                            } else {
                              callback(t('blackSystemPage.OSS.provideTweetOrUserId'));
                            }
                            numberValidator(_, value, 'User ID');
                          },
                        },
                        {
                          required: true,
                          validator: (_, value) => {
                            return numberValidator(_, value, 'User ID');
                          },
                        },
                      ]}
                    >
                      <Input variant="borderless" />
                    </Form.Item>
                  </>
                ) : // : keysForUserName.includes(selectedTweetType) ? (
                //   <>
                //     <Form.Item
                //       name="user_name"
                //       label="Enter user name"
                //       rules={[
                //         {
                //           required: true,
                //         },
                //       ]}
                //     >
                //       <Input variant="borderless" placeholder="# Enter a name..." />
                //     </Form.Item>
                //   </>
                // )
                null}
              </>
            ) : null}
            {selectedTweetType !== XEnum.tweets_of_last_seven_days && (
              <>
                <Form.Item label={t('blackSystemPage.newsApi.form.fromDate')} htmlFor="fromDate" name="fromDate">
                  <DatePicker
                    showTime
                    variant="borderless"
                    id="time"
                    format="YYYY-MM-DD HH:mm"
                    onChange={onFromDateChange}
                    disabledDate={(current) => disabledDate(current)}
                    minDate={min}
                    maxDate={max}
                    showHour={false}
                    showNow={false}
                  />
                </Form.Item>
                <Form.Item label={t('blackSystemPage.newsApi.form.toDate')} htmlFor="toDate" name="toDate">
                  <DatePicker
                    showTime
                    variant="borderless"
                    id="time"
                    format="YYYY-MM-DD HH:mm"
                    onChange={onToDateChange}
                    disabledDate={(current) => disabledDate(current, fromDateValue, true)}
                    minDate={min}
                    maxDate={max}
                    showHour={false}
                    showNow={false}
                  />
                </Form.Item>
              </>
            )}
          </FormComponent>
          <div className="resultAreas">
            {(downloadState.done || downloadState.state) && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                {downloadState.state ? (
                  <>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    <p>{downloadState.size}mb</p>
                  </>
                ) : (
                  downloadState.done && (
                    <>
                      <Progress type="circle" percent={100} size={30} />
                      <p>{t('blackSystemPage.successfullyDownloaded')}</p>
                    </>
                  )
                )}
              </div>
            )}
            {numberMessage !== '' && !isNumberLoading && (
              <div className="resultAreas">
                <div
                  className="resultArea"
                  style={{
                    ...(theme.colors.quoteBgc && {
                      borderTop: `1px solid ${theme.colors.quoteBgc}`,
                      borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                      color: `${theme.colors.quoteBgc}`,
                    }),
                  }}
                >
                  <Button
                    className="refetchBtn"
                    onClick={handleNumberReq}
                    style={{ ...(theme.colors.quoteBgc && { backgroundColor: `${theme.colors.quoteBgc}` }) }}
                  >
                    {t('blackSystemPage.refetchBtn')} <ReloadOutlined style={{ fontSize: '1.25' }} />
                  </Button>
                  <p>
                    {t('blackSystemPage.newsApi.numberMessage')}
                    {numberMessage}
                  </p>
                </div>
                <Progress
                  size={80}
                  type="circle"
                  percent={eventProgress.progress}
                  strokeColor={eventProgress.progress === 100 ? 'green' : `${theme.colors.quoteBgc}`}
                />
              </div>
            )}
            {/* {userNameOrTweetID !== '' && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                <p>{userNameOrTweetID}</p>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SocialMediaComponent;
